import type { AppDispatch } from "app/store";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";

import { fetchDrawingData } from "./fetchDrawingData";
import { setDrawingData, setDrawingPreview } from "./minimapSlice";
import type { Uuid } from "./types";

/**
 * Download a drawing and mirror its two rasterization phases into the slice.
 *
 * `fetchDrawingData` produces a low-resolution preview first (fast) and the
 * full-resolution tiles afterwards (slow). Those are stored in two independent
 * async states so they can succeed/fail independently:
 *
 *   - `previewAsyncState` drives every mini map's loading/error UI (and the
 *     large map's loading state) because it resolves well before the tiles.
 *   - `asyncState` holds the tiles, which only the large map consumes as a
 *     resolution upgrade.
 *
 * Both go `Loading` up front; the preview flips to `Success` as soon as it's
 * ready, then the tiles flip to `Success`. On failure the tiles state always
 * goes `Error`, but the preview only goes `Error` if it was never delivered — a
 * preview that already succeeded stays usable even if the background tiling
 * later fails, so the mini maps keep showing it.
 *
 * The download is intentionally NOT tied to an `AbortController`: aborting a
 * re-triggered/unmounted fetch would leave the states stranded between `Loading`
 * and a terminal status (see `useMiniMapData`). Letting it always run to a
 * terminal state keeps the slice consistent.
 */
export function downloadDrawingData({
    dispatch,
    drawingId,
    url,
    telemetrySource,
}: {
    dispatch: AppDispatch;
    drawingId: Uuid;
    url: string;
    telemetrySource: string;
}): Promise<void> {
    dispatch(setDrawingPreview({ drawingId, asyncState: { status: AsyncStatus.Loading } }));
    dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Loading } }));

    let previewDelivered = false;

    return fetchDrawingData(url, {
        onPreview: (preview) => {
            previewDelivered = true;
            dispatch(setDrawingPreview({ drawingId, asyncState: { status: AsyncStatus.Success, data: preview } }));
        },
    })
        .then((image) => {
            dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Success, data: image } }));
        })
        .catch((error) => {
            const e = error as Error;
            telemetry.trackCaughtException({
                source: telemetrySource,
                category: "render",
                error,
                severity: "error",
                extraAttributes: { drawingId },
            });
            dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Error, msg: e.message } }));
            // Only fail the preview if it never arrived; a delivered preview stays
            // usable even when the background tiling fails.
            if (!previewDelivered) {
                dispatch(setDrawingPreview({ drawingId, asyncState: { status: AsyncStatus.Error, msg: e.message } }));
            }
        });
}
